if (!customElements.get('mobile-sticky-cart')) {
  class MobileStickyCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.sectionId = this.dataset.section;
      this.infoRoot = document.getElementById(`ProductInfo-${this.sectionId}`);
      this.priceTarget = this.querySelector('[data-sticky-price]');
      this.quantityOutput = this.querySelector('[data-sticky-quantity]');
      this.quantityButtons = Array.from(this.querySelectorAll('[data-sticky-quantity-action]'));
      this.standardQuantity = this.querySelector('.mobile-sticky-cart__quantity');
      this.stickySubmit = this.querySelector('[data-sticky-submit]');
      this.stickySubmitLabel = this.querySelector('[data-sticky-submit-label]');
      this.stickySubmitSpinner = this.querySelector('[data-sticky-submit-spinner]');
      this.optionsToggle = this.querySelector('[data-sticky-options-toggle]');
      this.optionsSummary = this.querySelector('[data-sticky-options-summary]');
      this.optionsIcon = this.querySelector('[data-sticky-options-icon]');
      this.optionsPanel = this.querySelector('[data-sticky-options-panel]');
      this.customMainRow = this.querySelector('[data-sticky-custom-main-row]');
      this.customMainTitle = this.querySelector('[data-sticky-custom-main-title]');
      this.customMainPrice = this.querySelector('[data-sticky-custom-main-price]');
      this.customMainQuantity = this.querySelector('[data-sticky-custom-main-quantity]');
      this.optionList = this.querySelector('[data-sticky-option-list]');
      this.customHost = this.querySelector('[data-sticky-custom-host]');
      this.itemCountOutput = this.querySelector('[data-sticky-item-count]');
      this.errorTarget = this.querySelector('[data-sticky-error]');
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.optionMode = 'simple';
      this.batchRows = [];
      this.customAddonRows = [];
      this.customSourceInputs = [];
      this.customAddonSignature = '';
      this.lastCustomAddonPrice = 0;
      this.stickyQuantity = 0;
      this.syncingSourceQuantity = false;
      this.batchTouched = false;
      this.batchSubmitting = false;
      this.loadingSubmitLabel = null;
      this.pageOffsetHeight = null;
      this.sourceVariantId = null;
      this.dataset.controllerVersion = 'soldout-option-label-v16';

      this.handleMutation = this.handleMutation.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleQuantityClick = this.handleQuantityClick.bind(this);
      this.handleSourceQuantityChange = this.handleSourceQuantityChange.bind(this);
      this.handleSubmitClick = this.handleSubmitClick.bind(this);
      this.handleOptionsToggle = this.handleOptionsToggle.bind(this);
      this.handleOptionListClick = this.handleOptionListClick.bind(this);
      this.handleGpoInput = this.handleGpoInput.bind(this);
      this.handleGpoPriceUpdated = this.handleGpoPriceUpdated.bind(this);
      this.updatePageOffset = this.updatePageOffset.bind(this);

      this.quantityButtons.forEach((button) => button.addEventListener('click', this.handleQuantityClick));
      this.stickySubmit?.addEventListener('click', this.handleSubmitClick);
      this.optionsToggle?.addEventListener('click', this.handleOptionsToggle);
      this.optionList?.addEventListener('click', this.handleOptionListClick);
      document.addEventListener('globo.options.event.product.price.updated', this.handleGpoPriceUpdated);

      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, { threshold: 0 });
      this.mutationObserver = new MutationObserver(this.handleMutation);
      this.gpoPriceObserver = new MutationObserver(() => {
        if (this.optionMode !== 'custom') return;
        this.handleGpoPriceUpdated();
      });
      if (this.infoRoot) {
        this.mutationObserver.observe(this.infoRoot, {
          attributes: true,
          attributeFilter: ['aria-disabled', 'class', 'disabled', 'hidden'],
          characterData: true,
          childList: true,
          subtree: true
        });
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updatePageOffset);
        this.resizeObserver.observe(this);
      }

      if (this.mobileMedia.addEventListener) {
        this.mobileMedia.addEventListener('change', this.handleBreakpointChange);
      } else {
        this.mobileMedia.addListener(this.handleBreakpointChange);
      }
      window.addEventListener('resize', this.updatePageOffset);

      this.syncSourceElements();
      this.configureOptionMode();
      this.sync();
      this.setVisible(false);
    }

    disconnectedCallback() {
      this.restoreGpoApp();
      this.intersectionObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.gpoPriceObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.observedButton = null;
      this.lastIntersection = null;

      this.quantityButtons?.forEach((button) => button.removeEventListener('click', this.handleQuantityClick));
      this.stickySubmit?.removeEventListener('click', this.handleSubmitClick);
      this.optionsToggle?.removeEventListener('click', this.handleOptionsToggle);
      this.optionList?.removeEventListener('click', this.handleOptionListClick);
      this.quantityInput?.removeEventListener('input', this.handleSourceQuantityChange);
      this.quantityInput?.removeEventListener('change', this.handleSourceQuantityChange);
      this.gpoApp?.removeEventListener('input', this.handleGpoInput);
      this.gpoApp?.removeEventListener('change', this.handleGpoInput);
      document.removeEventListener('globo.options.event.product.price.updated', this.handleGpoPriceUpdated);

      if (this.mobileMedia?.removeEventListener) {
        this.mobileMedia.removeEventListener('change', this.handleBreakpointChange);
      } else {
        this.mobileMedia?.removeListener(this.handleBreakpointChange);
      }
      window.removeEventListener('resize', this.updatePageOffset);

      this.clearPageOffset();
      this.initialized = false;
    }

    handleMutation() {
      this.syncSourceElements();
      this.configureOptionMode();
      this.sync();
    }

    syncSourceElements() {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      const nextSourceButton = form?.querySelector('.product-form__submit[name="add"]') || null;
      const nextQuantityInput = document.getElementById(`Quantity-${this.sectionId}`);

      if (this.quantityInput !== nextQuantityInput) {
        const sourceInputReplaced = Boolean(this.quantityInput && nextQuantityInput);
        this.quantityInput?.removeEventListener('input', this.handleSourceQuantityChange);
        this.quantityInput?.removeEventListener('change', this.handleSourceQuantityChange);
        this.quantityInput = nextQuantityInput;
        this.quantityInput?.addEventListener('input', this.handleSourceQuantityChange);
        this.quantityInput?.addEventListener('change', this.handleSourceQuantityChange);
        if (sourceInputReplaced) this.stickyQuantity = 0;
      }

      this.productForm = form;
      this.sourceButton = nextSourceButton;
      this.observeSourceButton();

      if (this.sourceButton) this.hidden = false;
    }

    observeSourceButton() {
      if (!this.intersectionObserver || this.observedButton === this.sourceButton) return;
      if (this.observedButton) this.intersectionObserver.unobserve(this.observedButton);
      this.observedButton = this.sourceButton;
      if (this.observedButton) this.intersectionObserver.observe(this.observedButton);
    }

    configureOptionMode() {
      const variants = this.readVariants();
      const nextGpoApp = this.infoRoot?.querySelector('.gpo-app') || this.gpoApp || null;
      const manualInputs = nextGpoApp
        ? Array.from(nextGpoApp.querySelectorAll('input[gpo-data-variant-id][data-field-name]'))
          .filter((input) => input.dataset.unavailable !== 'true')
        : [];
      const gpoElements = this.readGpoElements(nextGpoApp);
      const hasCustomInputs = nextGpoApp
        ? Array.from(nextGpoApp.querySelectorAll('input, select, textarea'))
          .some((control) => this.isCustomGpoControl(control))
        : false;
      const nextMode = hasCustomInputs ? 'custom' : (manualInputs.length > 0 || variants.length > 1 ? 'batch' : 'simple');
      const selectedVariantId = this.productForm?.querySelector('[name="id"]')?.value || '';
      const signature = [
        nextMode,
        selectedVariantId,
        variants.map((variant) => variant.id).join(','),
        manualInputs.map((input) => input.getAttribute('gpo-data-variant-id')).join(',')
      ].join(':');
      const signatureChanged = signature !== this.optionSignature;
      const customInputsChanged = nextMode === 'custom'
        && (manualInputs.length !== this.customSourceInputs.length
          || manualInputs.some((input, index) => input !== this.customSourceInputs[index]));
      const customPanelWasOpen = this.optionMode === 'custom'
        && this.optionsPanel
        && !this.optionsPanel.hidden;

      if (this.gpoApp !== nextGpoApp) {
        this.restoreGpoApp();
        this.gpoApp?.removeEventListener('input', this.handleGpoInput);
        this.gpoApp?.removeEventListener('change', this.handleGpoInput);
        this.gpoPriceObserver?.disconnect();
        this.gpoApp = nextGpoApp;
        this.gpoApp?.addEventListener('input', this.handleGpoInput);
        this.gpoApp?.addEventListener('change', this.handleGpoInput);
        if (this.gpoApp) {
          this.gpoPriceObserver?.observe(this.gpoApp, {
            characterData: true,
            childList: true,
            subtree: true
          });
        }
      }

      if (!signatureChanged && !customInputsChanged) return;
      this.restoreGpoApp();
      this.clearCustomAddonGroupMarkers();
      this.optionSignature = signature;
      this.sourceVariantId = selectedVariantId;
      this.optionMode = nextMode;
      this.dataset.optionMode = nextMode;
      if (signatureChanged) {
        this.batchTouched = false;
        this.stickyQuantity = 0;
        this.lastCustomAddonPrice = 0;
        this.closeOptionsPanel();
      }

      if (nextMode === 'batch') {
        this.hideCustomMainRow();
        this.customAddonRows = [];
        this.customSourceInputs = [];
        this.batchRows = this.buildBatchRows(variants, manualInputs, gpoElements, 0);
        this.renderBatchRows();
      } else if (nextMode === 'custom') {
        this.batchRows = [];
        this.customSourceInputs = manualInputs.slice();
        this.customAddonRows = this.buildCustomAddonRows(manualInputs, gpoElements);
        this.customAddonSignature = this.customAddonRows
          .map((row) => `${row.fieldName}:${row.id}`)
          .join('|');
        this.renderCustomAddonRows();
        this.showCustomMainRow();
      } else {
        this.hideCustomMainRow();
        this.batchRows = [];
        this.customAddonRows = [];
        this.customSourceInputs = [];
        if (this.optionList) this.optionList.innerHTML = '';
      }

      this.applyModeLayout();
      if (!signatureChanged && customPanelWasOpen && nextMode === 'custom') {
        this.moveGpoAppToPanel();
      }
    }

    readVariants() {
      const script = this.querySelector('[data-sticky-variants]');
      if (!script?.textContent.trim()) return [];

      try {
        const variants = JSON.parse(script.textContent);
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        return [];
      }
    }

    getSelectedVariant(variants = this.readVariants()) {
      const selectedVariantId = Number(this.productForm?.querySelector('[name="id"]')?.value);
      return variants.find((variant) => Number(variant.id) === selectedVariantId) || variants[0] || null;
    }

    getCustomMainRowData() {
      const variant = this.getSelectedVariant();
      const productTitle = this.dataset.productTitle || '商品';
      const variantTitle = String(variant?.title || '').trim();
      const title = variantTitle && variantTitle !== 'Default Title'
        ? `${productTitle} / ${variantTitle}`
        : productTitle;

      return {
        title,
        unitPrice: Number(variant?.price) || 0,
        quantity: this.numberFromInput(this.stickyQuantity, 0)
      };
    }

    readGpoElements(gpoApp) {
      const optionSetId = gpoApp?.dataset.optionSetId;
      const elements = optionSetId ? window.GPOConfigs?.options?.[optionSetId]?.elements : null;
      return this.flattenGpoElements(elements || []);
    }

    flattenGpoElements(elements) {
      return elements.flatMap((element) => {
        if (element.type === 'group') return this.flattenGpoElements(element.elements || []);
        return [element];
      });
    }

    isCustomGpoElement(element) {
      const ignoredTypes = ['paragraph', 'divider', 'heading', 'spacer'];
      if (ignoredTypes.includes(element.type)) return false;

      const values = element.option_values || [];
      const isManualProductOnly = values.length > 0
        && values.every((value) => value.variant_id);
      return !isManualProductOnly;
    }

    isCustomGpoControl(control) {
      if (control.disabled) return false;
      if (control.matches('input[type="hidden"], input[type="button"], input[type="submit"], .gpo-quantity__input')) return false;
      if (control.hasAttribute('gpo-data-variant-id')) return false;

      const elementId = control.closest('[data-element-id]')?.dataset.elementId || '';
      const fieldName = control.getAttribute('data-field-name') || control.name || '';
      return !elementId.startsWith('product-link-') && !fieldName.startsWith('product-link-');
    }

    buildBatchRows(variants, manualInputs, gpoElements, initialVariantQuantity = 0) {
      const selectedVariantId = Number(this.productForm?.querySelector('[name="id"]')?.value);
      const selectedExists = variants.some((variant) => Number(variant.id) === selectedVariantId);
      const initialVariantId = selectedExists ? selectedVariantId : Number(variants[0]?.id);
      const variantRows = variants.map((variant) => ({
        kind: 'variant',
        id: Number(variant.id),
        title: variant.title,
        unitPrice: Number(variant.price) || 0,
        available: variant.available !== false,
        quantity: Number(variant.id) === initialVariantId ? initialVariantQuantity : 0,
        min: 0,
        max: this.numberFromInput(variant.quantity_rule?.max, Number.POSITIVE_INFINITY),
        step: this.numberFromInput(variant.quantity_rule?.increment, 1)
      }));

      if (manualInputs.length === 0) {
        return variantRows;
      }

      const baseVariant = variants.find((variant) => Number(variant.id) === selectedVariantId) || variants[0] || {};
      const valueByVariant = new Map();
      gpoElements.forEach((element) => {
        (element.option_values || []).forEach((value) => {
          if (!value.variant_id) return;
          valueByVariant.set(String(value.variant_id), { element, value });
        });
      });

      const rows = variants.length > 1 ? variantRows : [{
          kind: 'base',
          id: Number(baseVariant.id || selectedVariantId),
          title: this.dataset.productTitle || baseVariant.title || '商品',
          unitPrice: Number(baseVariant.price) || 0,
          available: baseVariant.available !== false && !this.sourceButton?.disabled,
          quantity: 0,
          min: 0,
          max: this.numberFromInput(baseVariant.quantity_rule?.max || this.quantityInput?.max, Number.POSITIVE_INFINITY),
          step: this.numberFromInput(baseVariant.quantity_rule?.increment || this.quantityInput?.step, 1),
          sourceQuantityInput: this.quantityInput
        }];

      manualInputs.forEach((input) => {
        const variantId = input.getAttribute('gpo-data-variant-id');
        const config = valueByVariant.get(String(variantId));
        const fieldName = input.dataset.fieldName || input.name;
        const quantityInput = this.gpoApp?.querySelector(
          `.gpo-quantity__input[data-parent-field-name="${CSS.escape(fieldName)}"]`
        );
        const selectedQuantity = input.checked
          ? this.numberFromInput(quantityInput?.value, 1)
          : 0;

        rows.push({
          kind: 'addon',
          id: Number(variantId),
          title: this.getAddonTitle(variantId, fieldName, config, input),
          fieldName,
          unitPrice: this.moneyToCents(config?.value?.variant_price),
          available: !input.disabled,
          quantity: selectedQuantity,
          min: 0,
          max: this.numberFromInput(quantityInput?.max, Number.POSITIVE_INFINITY),
          step: this.numberFromInput(quantityInput?.step, 1),
          sourceOptionInput: input,
          sourceQuantityInput: quantityInput
        });
      });

      return rows;
    }

    getAddonTitle(variantId, fieldName, config, input) {
      if (this.dataset.productHandle === 'fbt-370') {
        const titlesByVariant = {
          '53118975344954': '穴あきボール12個入り',
          '44162367390010': 'ダミーボールのスペア',
          '44162363097402': 'ボール置きのスペア'
        };
        const normalizedFieldName = String(fieldName || '')
          .replace(/\s/g, '')
          .replace(/[０-９]/g, (digit) => String('０１２３４５６７８９'.indexOf(digit)));
        const titlesByFieldName = {
          'バッティング練習ボール12個入り': '穴あきボール12個入り',
          'ダミーボールスペア': 'ダミーボールのスペア',
          'スペアボール': 'ボール置きのスペア'
        };
        const fixedTitle = titlesByVariant[String(variantId)] || titlesByFieldName[normalizedFieldName];
        if (fixedTitle) return fixedTitle;
      }

      const inputValue = String(input?.value || '').trim();
      const optionName = String(fieldName || config?.element?.label || '').trim();
      if (this.optionMode === 'custom' && inputValue === 'する' && optionName) {
        return optionName.replace(/も同時に注文する\s*$/, '').trim() || optionName;
      }

      return inputValue || config?.value?.value || optionName;
    }

    createCustomAddonRow(input, valueByVariant) {
      const variantId = input.getAttribute('gpo-data-variant-id');
      if (!variantId || input.disabled) return null;

      const group = input.closest('.gpo-form__group');
      if (!group) return null;
      const fieldName = input.dataset.fieldName || input.name || '';
      const quantityInput = this.gpoApp?.querySelector(
        `.gpo-quantity__input[data-parent-field-name="${CSS.escape(fieldName)}"]`
      );
      const offInput = input.type === 'radio'
        ? Array.from(group?.querySelectorAll('input[type="radio"]') || [])
          .find((candidate) => candidate !== input && !candidate.hasAttribute('gpo-data-variant-id')) || null
        : null;

      if (input.type === 'radio' && !offInput) return null;
      if (!['radio', 'checkbox'].includes(input.type)) return null;

      const config = valueByVariant.get(String(variantId));
      return {
        kind: 'custom-addon',
        id: Number(variantId),
        fieldName,
        title: this.getAddonTitle(variantId, fieldName, config, input),
        unitPrice: this.moneyToCents(config?.value?.variant_price),
        available: !input.disabled,
        quantity: input.checked ? this.numberFromInput(quantityInput?.value, 1) : 0,
        min: 0,
        max: this.numberFromInput(quantityInput?.max, input.type === 'radio' ? 1 : Number.POSITIVE_INFINITY),
        step: this.numberFromInput(quantityInput?.step, 1),
        sourceOptionInput: input,
        sourceOffInput: offInput,
        sourceQuantityInput: quantityInput,
        sourceGroup: group
      };
    }

    buildCustomAddonRows(manualInputs, gpoElements) {
      const valueByVariant = new Map();
      gpoElements.forEach((element) => {
        (element.option_values || []).forEach((value) => {
          if (value.variant_id) valueByVariant.set(String(value.variant_id), { element, value });
        });
      });

      const rows = [];
      const seen = new Set();
      manualInputs.forEach((input) => {
        const row = this.createCustomAddonRow(input, valueByVariant);
        if (!row) return;
        const key = `${row.fieldName}:${row.id}`;
        if (seen.has(key)) return;
        seen.add(key);
        rows.push(row);
      });

      const rowsByGroup = new Map();
      rows.forEach((row) => {
        const groupRows = rowsByGroup.get(row.sourceGroup) || [];
        groupRows.push(row);
        rowsByGroup.set(row.sourceGroup, groupRows);
      });

      return rows.filter((row) => this.customAddonGroupIsFullyControlled(
        row.sourceGroup,
        rowsByGroup.get(row.sourceGroup) || []
      ));
    }

    customAddonGroupIsFullyControlled(group, rows) {
      if (!group || rows.length === 0) return false;
      const controlled = new Set();
      rows.forEach((row) => {
        controlled.add(row.sourceOptionInput);
        if (row.sourceOffInput) controlled.add(row.sourceOffInput);
        if (row.sourceQuantityInput) controlled.add(row.sourceQuantityInput);
      });

      return Array.from(group.querySelectorAll('input, select, textarea')).every((control) => {
        if (control.disabled) return true;
        if (['hidden', 'button', 'submit'].includes(control.type)) return true;
        return controlled.has(control);
      });
    }

    renderBatchRows() {
      if (!this.optionList) return;
      this.optionList.innerHTML = '';
      const showFtrpAddonDivider = this.dataset.productHandle === 'ftrp'
        && this.batchRows.some((row) => row.kind === 'variant')
        && this.batchRows.some((row) => row.kind === 'addon');
      let addonDividerRendered = false;

      this.batchRows.forEach((row, index) => {
        if (showFtrpAddonDivider && !addonDividerRendered && row.kind === 'addon') {
          const divider = document.createElement('p');
          divider.className = 'mobile-sticky-cart__option-divider';
          divider.dataset.stickyOptionDivider = '';
          divider.textContent = 'グリップソックスもおすすめ';
          this.optionList.appendChild(divider);
          addonDividerRendered = true;
        }

        const element = document.createElement('div');
        element.className = 'mobile-sticky-cart__option-row';
        element.dataset.stickyOptionRow = '';
        element.dataset.rowIndex = String(index);

        const title = document.createElement(row.kind === 'variant' ? 'button' : 'span');
        title.className = 'mobile-sticky-cart__option-title';
        title.dataset.stickyOptionTitle = '';
        title.textContent = row.title;
        if (row.kind === 'variant') {
          title.type = 'button';
          title.dataset.stickyVariantSelect = '';
        }

        const price = document.createElement('span');
        price.className = 'mobile-sticky-cart__option-price';
        price.textContent = this.formatMoney(row.unitPrice);

        const stepper = document.createElement('span');
        stepper.className = 'mobile-sticky-cart__option-stepper';

        const minus = this.createRowButton('minus', '数量を減らす');
        const quantity = document.createElement('output');
        quantity.dataset.stickyOptionQuantity = '';
        quantity.textContent = String(row.quantity);
        const plus = this.createRowButton('plus', '数量を増やす');

        stepper.append(minus, quantity, plus);
        element.append(title, price, stepper);
        this.optionList.appendChild(element);
        this.updateBatchRow(element, row);
      });

      this.updateBatchSummary();
    }

    renderCustomAddonRows() {
      if (!this.optionList) return;
      this.optionList.innerHTML = '';

      this.customAddonRows.forEach((row, index) => {
        const element = document.createElement('div');
        element.className = 'mobile-sticky-cart__option-row';
        element.dataset.stickyCustomAddonRow = '';
        element.dataset.rowIndex = String(index);

        const title = document.createElement('span');
        title.className = 'mobile-sticky-cart__option-title';
        title.textContent = row.title;
        const price = document.createElement('span');
        price.className = 'mobile-sticky-cart__option-price';
        price.textContent = this.formatMoney(row.unitPrice);
        const stepper = document.createElement('span');
        stepper.className = 'mobile-sticky-cart__option-stepper';
        const minus = this.createRowButton('minus', '数量を減らす');
        const quantity = document.createElement('output');
        quantity.dataset.stickyOptionQuantity = '';
        quantity.textContent = String(row.quantity);
        const plus = this.createRowButton('plus', '数量を増やす');

        stepper.append(minus, quantity, plus);
        element.append(title, price, stepper);
        this.optionList.appendChild(element);
        this.updateBatchRow(element, row);
        if (row.sourceGroup) row.sourceGroup.dataset.stickyControlledAddonGroup = 'true';
      });
    }

    showCustomMainRow() {
      if (this.optionMode !== 'custom' || !this.customMainRow) return;
      this.customMainRow.hidden = false;
      this.updateCustomMainRow();
    }

    hideCustomMainRow() {
      if (this.customMainRow) this.customMainRow.hidden = true;
    }

    updateCustomMainRow() {
      if (this.optionMode !== 'custom') return;
      const row = this.getCustomMainRowData();
      if (this.customMainTitle) this.customMainTitle.textContent = row.title;
      if (this.customMainPrice) this.customMainPrice.textContent = this.formatMoney(row.unitPrice);
      if (this.customMainQuantity) this.customMainQuantity.textContent = String(row.quantity);
    }

    createRowButton(action, label) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.stickyRowAction = action;
      button.setAttribute('aria-label', label);
      button.textContent = action === 'minus' ? '−' : '+';
      return button;
    }

    applyModeLayout() {
      const hasOptions = this.optionMode !== 'simple';
      if (this.optionsToggle) this.optionsToggle.hidden = !hasOptions;
      if (!hasOptions && this.optionsPanel) this.optionsPanel.hidden = true;

      if (this.optionMode === 'batch') {
        if (this.standardQuantity) this.standardQuantity.hidden = true;
        if (this.itemCountOutput) this.itemCountOutput.hidden = false;
        this.updateBatchSummary();
      } else {
        if (this.standardQuantity) this.standardQuantity.hidden = false;
        if (this.itemCountOutput) this.itemCountOutput.hidden = true;
        if (this.optionMode === 'custom') {
          this.updateCustomSummary();
          this.updateCustomMainRow();
        }
      }
    }

    handleOptionsToggle() {
      if (!this.optionsPanel || this.optionMode === 'simple' || this.optionMode === 'custom') return;
      const shouldOpen = this.optionsToggle?.getAttribute('aria-expanded') !== 'true';

      if (shouldOpen) {
        if (this.optionMode === 'custom') this.moveGpoAppToPanel();
        this.optionsPanel.hidden = false;
        this.optionsToggle?.setAttribute('aria-expanded', 'true');
        if (this.optionsIcon) this.optionsIcon.textContent = '−';
      } else {
        this.closeOptionsPanel();
      }

      window.requestAnimationFrame(this.updatePageOffset);
    }

    closeOptionsPanel() {
      if (this.optionsPanel) this.optionsPanel.hidden = true;
      this.optionsToggle?.setAttribute('aria-expanded', 'false');
      if (this.optionsIcon) this.optionsIcon.textContent = '+';
      if (this.optionMode === 'custom') this.restoreGpoApp();
    }

    moveGpoAppToPanel() {
      if (!this.gpoApp || !this.customHost || this.gpoApp.parentNode === this.customHost) return;
      this.gpoPlaceholder = document.createComment('mobile-sticky-cart-gpo-placeholder');
      this.gpoApp.parentNode.insertBefore(this.gpoPlaceholder, this.gpoApp);
      this.customHost.appendChild(this.gpoApp);
    }

    restoreGpoApp() {
      if (!this.gpoPlaceholder || !this.gpoApp) return;
      this.gpoPlaceholder.parentNode?.insertBefore(this.gpoApp, this.gpoPlaceholder);
      this.gpoPlaceholder.remove();
      this.gpoPlaceholder = null;
    }

    clearCustomAddonGroupMarkers() {
      (this.customAddonRows || []).forEach((row) => {
        if (row.sourceGroup?.dataset) delete row.sourceGroup.dataset.stickyControlledAddonGroup;
      });
    }

    handleOptionListClick(event) {
      const customRowElement = event.target.closest('[data-sticky-custom-addon-row]');
      const customButton = event.target.closest('[data-sticky-row-action]');
      if (this.optionMode === 'custom' && customRowElement && customButton && !customButton.disabled) {
        const index = Number(customRowElement.dataset.rowIndex);
        const row = this.customAddonRows[index];
        if (!row) return;
        const direction = customButton.dataset.stickyRowAction === 'minus' ? -1 : 1;
        this.syncCustomAddonToGpo(row, row.quantity + (row.step * direction));
        this.updateBatchRow(customRowElement, row);
        this.syncCustomPrice();
        return;
      }

      const variantSelect = event.target.closest('[data-sticky-variant-select]');
      if (variantSelect) {
        const rowElement = variantSelect.closest('[data-sticky-option-row]');
        this.selectVariantRow(Number(rowElement?.dataset.rowIndex));
        return;
      }

      const button = event.target.closest('[data-sticky-row-action]');
      const rowElement = button?.closest('[data-sticky-option-row]');
      if (!button || !rowElement || button.disabled) return;

      const row = this.batchRows[Number(rowElement.dataset.rowIndex)];
      if (!row) return;

      const direction = button.dataset.stickyRowAction === 'minus' ? -1 : 1;
      const next = Math.min(row.max, Math.max(row.min, row.quantity + (row.step * direction)));
      if (next === row.quantity) return;

      row.quantity = next;
      this.batchTouched = true;
      this.syncBatchRowToSource(row);
      this.updateBatchRow(rowElement, row);
      this.updateBatchSummary();
    }

    syncCustomAddonToGpo(row, quantity) {
      const next = Math.min(row.max, Math.max(row.min, quantity));
      row.quantity = next;

      if (next > 0) {
        if (!row.sourceOptionInput.checked) row.sourceOptionInput.click();
        if (row.sourceQuantityInput) {
          row.sourceQuantityInput.value = String(next);
          row.sourceQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          row.sourceQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (row.sourceOptionInput.type === 'checkbox') {
        if (row.sourceOptionInput.checked) row.sourceOptionInput.click();
      } else if (row.sourceOffInput && !row.sourceOffInput.checked) {
        row.sourceOffInput.click();
      }
    }

    syncCustomAddonRowsFromGpo() {
      (this.customAddonRows || []).forEach((row, index) => {
        row.quantity = row.sourceOptionInput.checked
          ? this.numberFromInput(row.sourceQuantityInput?.value, 1)
          : 0;
        const element = this.optionList?.querySelector(
          `[data-sticky-custom-addon-row][data-row-index="${index}"]`
        );
        if (element) this.updateBatchRow(element, row);
      });
    }

    selectVariantRow(selectedIndex) {
      const selectedRow = this.batchRows[selectedIndex];
      if (selectedRow?.kind !== 'variant' || !selectedRow.available) return;

      this.batchRows.forEach((row, index) => {
        if (row.kind !== 'variant') return;
        row.quantity = index === selectedIndex ? row.quantity : 0;
        const element = this.optionList?.querySelector(`[data-row-index="${index}"]`);
        if (element) this.updateBatchRow(element, row);
      });
      this.batchTouched = true;
      this.updateBatchSummary();
    }

    syncBatchRowToSource(row) {
      if (row.kind === 'base' && row.sourceQuantityInput) {
        if (row.quantity <= 0) return;
        row.sourceQuantityInput.value = String(row.quantity);
        row.sourceQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        row.sourceQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      if (row.kind !== 'addon' || !row.sourceOptionInput) return;
      const shouldSelect = row.quantity > 0;
      if (row.sourceOptionInput.checked !== shouldSelect) {
        row.sourceOptionInput.checked = shouldSelect;
        row.sourceOptionInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (row.sourceQuantityInput && shouldSelect) {
        row.sourceQuantityInput.value = String(row.quantity);
        row.sourceQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        row.sourceQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateBatchRow(element, row) {
      const output = element.querySelector('[data-sticky-option-quantity]');
      const minus = element.querySelector('[data-sticky-row-action="minus"]');
      const plus = element.querySelector('[data-sticky-row-action="plus"]');
      const variantSelect = element.querySelector('[data-sticky-variant-select]');
      if (output) output.textContent = String(row.quantity);
      if (minus) minus.disabled = !row.available || row.quantity <= row.min;
      if (plus) plus.disabled = !row.available || row.quantity >= row.max;
      if (variantSelect) {
        variantSelect.disabled = !row.available;
        variantSelect.setAttribute('aria-pressed', String(row.quantity > 0));
      }
      element.classList.toggle('is-unavailable', !row.available);
    }

    updateBatchSummary() {
      if (this.optionMode !== 'batch') return;
      const selected = this.batchRows.filter((row) => row.quantity > 0);
      const totalQuantity = selected.reduce((total, row) => total + row.quantity, 0);
      const totalPrice = selected.reduce((total, row) => total + (row.unitPrice * row.quantity), 0);
      const unavailableSelected = selected.some((row) => !row.available);
      const mainAvailable = this.batchRows.some(
        (row) => ['base', 'variant'].includes(row.kind) && row.available
      );
      const purchasableSelection = totalQuantity > 0 && !unavailableSelected;

      if (this.optionsSummary) {
        this.optionsSummary.textContent = selected.length
          ? selected.map((row) => `${row.title}×${row.quantity}`).join('、')
          : '種類と数量を選択';
      }
      if (this.itemCountOutput) this.itemCountOutput.textContent = `${totalQuantity}点`;
      if (this.priceTarget) {
        this.priceTarget.textContent = this.formatMoney(totalPrice);
        this.priceTarget.hidden = false;
      }
      if (this.stickySubmit) {
        const disabled = this.batchSubmitting || totalQuantity === 0 || unavailableSelected;
        this.stickySubmit.disabled = disabled;
        this.stickySubmit.setAttribute('aria-disabled', String(disabled));
      }
      if (this.stickySubmitLabel && !this.stickySubmit?.classList.contains('is-loading')) {
        this.stickySubmitLabel.textContent = purchasableSelection || mainAvailable
          ? (window.variantStrings?.addToCart || 'カートに追加する')
          : (window.variantStrings?.soldOut || '在庫切れ');
      }
    }

    handleGpoInput() {
      if (this.optionMode !== 'custom') return;
      this.updateCustomSummary();
      this.handleGpoPriceUpdated();
      window.requestAnimationFrame(() => this.handleGpoPriceUpdated());
    }

    handleGpoPriceUpdated() {
      if (this.optionMode !== 'custom') return;
      this.syncCustomAddonRowsFromGpo();
      this.syncCustomPrice();
    }

    updateCustomSummary() {
      if (!this.optionsSummary) return;
      this.optionsSummary.textContent = this.customOptionsComplete()
        ? 'オプション：入力済み'
        : 'オプションを入力';
    }

    customOptionsComplete() {
      if (!this.gpoApp) return false;
      const requiredGroups = Array.from(this.gpoApp.querySelectorAll('.gpo-form__group'))
        .filter((group) => !group.hidden && group.querySelector('.gpo-label.required'));
      if (requiredGroups.length === 0) return false;

      return requiredGroups.every((group) => {
        const inputs = Array.from(group.querySelectorAll('input, select, textarea'))
          .filter((input) => !input.disabled && input.type !== 'hidden');
        if (inputs.some((input) => ['radio', 'checkbox'].includes(input.type))) {
          return inputs.some((input) => input.checked);
        }
        return inputs.some((input) => String(input.value || '').trim() !== '');
      });
    }

    sync() {
      if (this.optionMode === 'batch') {
        this.updateBatchSummary();
        window.requestAnimationFrame(this.updatePageOffset);
        return;
      }

      this.syncQuantity();
      this.syncPrice();
      this.syncButton();
      if (this.optionMode === 'custom') {
        this.updateCustomSummary();
        this.updateCustomMainRow();
      }
      window.requestAnimationFrame(this.updatePageOffset);
    }

    handleSourceQuantityChange() {
      if (this.syncingSourceQuantity) return;

      if (this.optionMode === 'batch') {
        const baseRow = this.batchRows.find((row) => row.kind === 'base');
        if (baseRow) {
          baseRow.quantity = this.numberFromInput(this.quantityInput?.value, baseRow.quantity);
          const rowElement = this.optionList?.querySelector(`[data-row-index="${this.batchRows.indexOf(baseRow)}"]`);
          if (rowElement) this.updateBatchRow(rowElement, baseRow);
          this.updateBatchSummary();
        }
        return;
      }

      this.stickyQuantity = this.numberFromInput(this.quantityInput?.value, 0);
      this.syncQuantity();
      this.syncPrice();
      this.syncButton();
    }

    syncQuantity() {
      if (!this.quantityOutput) return;

      if (!this.quantityInput) {
        this.quantityOutput.textContent = '0';
        this.quantityButtons.forEach((button) => { button.disabled = true; });
        return;
      }

      const value = this.numberFromInput(this.stickyQuantity, 0);
      const min = 0;
      const max = this.numberFromInput(this.quantityInput.max, Number.POSITIVE_INFINITY);
      this.quantityOutput.textContent = String(value);

      this.quantityButtons.forEach((button) => {
        const isMinus = button.dataset.stickyQuantityAction === 'minus';
        const atLimit = isMinus ? value <= min : value >= max;
        button.disabled = this.quantityInput.disabled || atLimit;
      });
    }

    syncPrice() {
      if (!this.priceTarget || this.optionMode === 'batch') return;
      if (this.optionMode === 'custom') {
        this.syncCustomPrice();
        return;
      }
      const sourcePrice = document.getElementById(`price-${this.sectionId}`);
      const unavailable = !sourcePrice || sourcePrice.classList.contains('visibility-hidden');

      if (unavailable) {
        this.priceTarget.innerHTML = '';
        this.priceTarget.hidden = true;
        return;
      }

      this.priceTarget.innerHTML = sourcePrice.innerHTML;
      const quantity = this.numberFromInput(this.stickyQuantity, 0);
      this.priceTarget.querySelectorAll('.price-item--regular, .price-item--sale').forEach((priceItem) => {
        const walker = document.createTreeWalker(priceItem, NodeFilter.SHOW_TEXT);
        const textNodes = [];
        while (walker.nextNode()) textNodes.push(walker.currentNode);

        textNodes.forEach((textNode) => {
          textNode.textContent = this.multiplyPriceText(textNode.textContent, quantity);
        });
      });
      this.priceTarget.hidden = false;
    }

    syncCustomPrice() {
      if (!this.priceTarget) return;
      const row = this.getCustomMainRowData();
      const addonMoney = this.gpoApp?.querySelector('.gpo-total-text .money');

      if (addonMoney) {
        this.lastCustomAddonPrice = this.moneyTextToCents(addonMoney.textContent || '');
      }

      const unitPrice = row.unitPrice + this.numberFromInput(this.lastCustomAddonPrice, 0);
      const displayQuantity = row.quantity > 0 ? row.quantity : 1;
      this.priceTarget.textContent = this.formatMoney(unitPrice * displayQuantity);
      this.priceTarget.hidden = false;
      this.updateCustomMainRow();
    }

    multiplyPriceText(text, quantity) {
      const match = text.match(/\d[\d,]*(?:\.\d+)?/);
      if (!match) return text;

      const unitPrice = Number(match[0].replace(/,/g, ''));
      if (!Number.isFinite(unitPrice)) return text;

      const decimalPlaces = match[0].includes('.') ? match[0].split('.').pop().length : 0;
      const total = unitPrice * quantity;
      const formattedTotal = total.toLocaleString('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      });

      return text.replace(match[0], formattedTotal);
    }

    syncButton() {
      if (!this.stickySubmit || this.optionMode === 'batch') return;

      if (!this.sourceButton) {
        this.stickySubmit.disabled = true;
        this.stickySubmit.setAttribute('aria-disabled', 'true');
        this.setVisible(false);
        return;
      }

      const disabled = this.sourceButton.disabled
        || this.sourceButton.getAttribute('aria-disabled') === 'true'
        || this.numberFromInput(this.stickyQuantity, 0) <= 0;
      const sourceLabel = this.sourceButton.querySelector('span');
      const label = sourceLabel?.textContent.trim();

      this.stickySubmit.disabled = disabled;
      this.stickySubmit.setAttribute('aria-disabled', String(disabled));
      const isLoading = this.sourceButton.classList.contains('loading');
      this.setSubmitLoading(isLoading);
      if (label && this.stickySubmitLabel && !isLoading) this.stickySubmitLabel.textContent = label;
    }

    setSubmitLoading(isLoading) {
      if (!this.stickySubmit) return;

      this.stickySubmit.classList.toggle('is-loading', isLoading);
      if (isLoading) {
        this.stickySubmit.setAttribute('aria-busy', 'true');
      } else {
        this.stickySubmit.removeAttribute('aria-busy');
      }
      if (this.stickySubmitSpinner) this.stickySubmitSpinner.hidden = !isLoading;

      if (isLoading) {
        if (this.loadingSubmitLabel === null) {
          this.loadingSubmitLabel = this.stickySubmitLabel?.textContent.trim() || '';
        }
        if (this.stickySubmitLabel) this.stickySubmitLabel.textContent = 'カートに追加中…';
      } else if (this.loadingSubmitLabel !== null) {
        if (this.stickySubmitLabel) this.stickySubmitLabel.textContent = this.loadingSubmitLabel;
        this.loadingSubmitLabel = null;
      }
    }

    handleQuantityClick(event) {
      this.syncSourceElements();
      if (!this.quantityInput || event.currentTarget.disabled) return;

      const current = this.numberFromInput(this.stickyQuantity, 0);
      const min = 0;
      const max = this.numberFromInput(this.quantityInput.max, Number.POSITIVE_INFINITY);
      const step = this.numberFromInput(this.quantityInput.step, 1);
      const direction = event.currentTarget.dataset.stickyQuantityAction === 'minus' ? -1 : 1;
      const next = Math.min(max, Math.max(min, current + (step * direction)));

      if (next === current) return;
      this.stickyQuantity = next;
      if (next > 0) this.writeQuantityToSource(next);
      this.syncQuantity();
      this.syncPrice();
      this.syncButton();
    }

    writeQuantityToSource(quantity) {
      if (!this.quantityInput || quantity <= 0) return;

      this.syncingSourceQuantity = true;
      try {
        this.quantityInput.value = String(quantity);
        this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        this.syncingSourceQuantity = false;
      }
    }

    handleSubmitClick() {
      this.syncSourceElements();
      if (this.optionMode === 'batch') {
        this.submitBatch();
        return;
      }

      this.syncButton();
      if (!this.sourceButton || this.stickySubmit.disabled) return;

      this.sourceButton.click();
      window.requestAnimationFrame(() => {
        this.syncButton();
        this.reopenCustomPanelForErrors();
      });
    }

    reopenCustomPanelForErrors() {
      if (this.optionMode !== 'custom' || !this.gpoApp?.querySelector('.has-error')) return;
      if (this.optionsPanel) this.optionsPanel.hidden = false;
      this.optionsToggle?.setAttribute('aria-expanded', 'true');
      if (this.optionsIcon) this.optionsIcon.textContent = '−';
      this.setVisible(true);
    }

    async submitBatch() {
      if (this.batchSubmitting || this.stickySubmit?.disabled) return;
      const items = this.buildBatchItems();
      if (items.length === 0) return;

      this.batchSubmitting = true;
      this.setBatchError('');
      this.setSubmitLoading(true);
      this.updateBatchSummary();

      const payload = { items };
      const cartRenderer = this.getCartRenderer();
      const sectionIds = this.getNotificationSectionIds(cartRenderer);
      if (sectionIds.length > 0) {
        payload.sections = sectionIds;
        payload.sections_url = this.getSectionsUrl();
      }
      const cartBefore = cartRenderer ? await this.fetchCartState() : null;

      try {
        let response;
        try {
          const root = window.Shopify?.routes?.root || '/';
          const cartAddUrl = `${root.endsWith('/') ? root : `${root}/`}cart/add.js`;
          response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(payload)
          });
        } catch (error) {
          console.error(error);
          const rendered = await this.handleAmbiguousBatchResult(cartRenderer, cartBefore, items);
          if (!rendered) this.redirectToCart();
          return;
        }

        if (!response.ok) {
          let result = {};
          try {
            result = await response.json();
          } catch (_error) {
            // Use the generic message when Shopify does not return JSON.
          }
          this.setBatchError(result.description || result.message || 'カートに追加できませんでした');
          return;
        }

        let result;
        try {
          result = await response.json();
        } catch (error) {
          console.error(error);
          const rendered = await this.handleAmbiguousBatchResult(cartRenderer, cartBefore, items);
          if (!rendered) this.redirectToCart();
          return;
        }

        if (!cartRenderer || !this.renderBatchNotification(cartRenderer, result, items)) {
          this.redirectToCart();
        }
      } finally {
        this.batchSubmitting = false;
        this.setSubmitLoading(false);
        this.updateBatchSummary();
      }
    }

    getCartRenderer() {
      const renderer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      return renderer
        && typeof renderer.getSectionsToRender === 'function'
        && typeof renderer.renderContents === 'function'
        ? renderer
        : null;
    }

    getNotificationSectionIds(renderer) {
      if (!renderer) return [];
      return renderer.getSectionsToRender()
        .map((section) => section.id)
        .filter(Boolean);
    }

    getSectionsUrl() {
      const pathname = window.location.pathname;
      return pathname?.startsWith('/') ? pathname : '/';
    }

    redirectToCart() {
      window.location.assign(window.routes?.cart_url || '/cart');
    }

    async fetchCartState() {
      const root = window.Shopify?.routes?.root || '/';
      const cartUrl = `${root.endsWith('/') ? root : `${root}/`}cart.js`;

      try {
        const response = await fetch(cartUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return null;
        const cart = await response.json();
        return Array.isArray(cart?.items) ? cart : null;
      } catch (_error) {
        return null;
      }
    }

    async handleAmbiguousBatchResult(renderer, cartBefore, requestedItems) {
      if (!renderer || !cartBefore) return false;
      const cartAfter = await this.fetchCartState();
      if (!this.batchWasAdded(cartBefore, cartAfter, requestedItems)) return false;
      return this.renderVerifiedBatchNotification(renderer, cartAfter, requestedItems);
    }

    batchWasAdded(cartBefore, cartAfter, requestedItems) {
      if (!cartBefore || !cartAfter) return false;
      const beforeQuantities = this.cartQuantitiesBySignature(cartBefore.items);
      const afterQuantities = this.cartQuantitiesBySignature(cartAfter.items);
      const requestedQuantities = this.cartQuantitiesBySignature(requestedItems);

      return Array.from(requestedQuantities.entries()).every(([signature, quantity]) => {
        const before = beforeQuantities.get(signature) || 0;
        const after = afterQuantities.get(signature) || 0;
        return after - before >= quantity;
      });
    }

    cartQuantitiesBySignature(items) {
      const quantities = new Map();
      (items || []).forEach((item) => {
        const signature = this.cartItemSignature(item);
        const quantity = Number(item.quantity) || 0;
        quantities.set(signature, (quantities.get(signature) || 0) + quantity);
      });
      return quantities;
    }

    cartItemSignature(item) {
      const variantId = Number(item.variant_id || item.id) || 0;
      const properties = Object.entries(item.properties || {})
        .filter(([, value]) => value !== null && value !== '')
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, value]) => [key, String(value)]);
      const sellingPlan = item.selling_plan
        || item.selling_plan_allocation?.selling_plan?.id
        || '';
      return JSON.stringify([variantId, properties, String(sellingPlan)]);
    }

    findRepresentativeCartItem(cartItems, requestedItems) {
      if (!Array.isArray(cartItems) || cartItems.length === 0) return null;
      const mainRequest = requestedItems.find((item) => !item.properties?._gpo_parent_product_group)
        || requestedItems[0];
      const signature = mainRequest ? this.cartItemSignature(mainRequest) : '';
      return cartItems.find((item) => this.cartItemSignature(item) === signature) || cartItems[0];
    }

    renderBatchNotification(renderer, result, requestedItems) {
      const representative = this.findRepresentativeCartItem(result?.items, requestedItems);
      if (!representative?.key || !result?.sections) return false;

      try {
        this.closeOptionsPanel();
        renderer.renderContents({ ...result, key: representative.key });
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    async renderVerifiedBatchNotification(renderer, cart, requestedItems) {
      const representative = this.findRepresentativeCartItem(cart.items, requestedItems);
      if (!representative?.key) return false;
      const sections = await this.fetchNotificationSections(renderer);
      if (!sections) return false;

      return this.renderBatchNotification(renderer, {
        ...cart,
        items: [representative],
        key: representative.key,
        sections
      }, requestedItems);
    }

    async fetchNotificationSections(renderer) {
      const sectionIds = this.getNotificationSectionIds(renderer);
      if (sectionIds.length === 0) return null;
      const url = `${this.getSectionsUrl()}?sections=${encodeURIComponent(sectionIds.join(','))}`;

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return null;
        return await response.json();
      } catch (_error) {
        return null;
      }
    }

    buildBatchItems() {
      const selectedRows = this.batchRows.filter((row) => row.quantity > 0 && row.available);
      if (selectedRows.length === 0) return [];

      const sharedProperties = this.readLineItemProperties();
      const sellingPlan = this.productForm ? new FormData(this.productForm).get('selling_plan') : null;
      const hasAddons = selectedRows.some((row) => row.kind === 'addon');
      const hasMainProduct = selectedRows.some((row) => row.kind === 'variant' || row.kind === 'base');
      const groupId = hasAddons && hasMainProduct
        ? (sharedProperties._gpo_product_group || `mobile-sticky-${Date.now()}`)
        : null;

      return selectedRows.map((row) => {
        const item = { id: row.id, quantity: row.quantity };
        let properties = { ...sharedProperties };

        if (row.kind === 'addon' && groupId) {
          properties = {
            _gpo_parent_product_group: groupId,
            _gpo_field_name: row.fieldName
          };
        } else if (row.kind === 'addon') {
          properties = {};
        } else if (hasAddons) {
          properties._gpo_product_group = groupId;
        }

        if (Object.keys(properties).length > 0) item.properties = properties;
        if (sellingPlan) item.selling_plan = sellingPlan;
        return item;
      });
    }

    readLineItemProperties() {
      if (!this.productForm) return {};
      const properties = {};
      const formData = new FormData(this.productForm);

      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (!match || value === '') return;
        properties[match[1]] = String(value);
      });
      return properties;
    }

    setBatchError(message) {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = !message;
    }

    handleIntersection(entries) {
      const entry = entries[0];
      if (!entry) return;
      this.lastIntersection = entry;
      const passedAbove = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      const customPanelOpen = this.optionMode === 'custom' && this.optionsPanel && !this.optionsPanel.hidden;
      this.setVisible(this.mobileMedia.matches && (passedAbove || customPanelOpen));
    }

    handleBreakpointChange() {
      if (!this.mobileMedia.matches) {
        this.closeOptionsPanel();
        this.setVisible(false);
        return;
      }

      if (this.lastIntersection) this.handleIntersection([this.lastIntersection]);
    }

    setVisible(visible) {
      const shouldShow = Boolean(
        visible && this.sourceButton && this.mobileMedia.matches && this.optionMode !== 'custom'
      );
      this.classList.toggle('is-visible', shouldShow);
      this.setAttribute('aria-hidden', String(!shouldShow));
      this.toggleAttribute('inert', !shouldShow);

      if (shouldShow) {
        document.body.dataset.mobileStickyCartOwner = this.sectionId;
        document.body.classList.add('mobile-sticky-cart-visible');
        window.requestAnimationFrame(this.updatePageOffset);
      } else {
        this.closeOptionsPanel();
        this.clearPageOffset();
      }
    }

    updatePageOffset() {
      if (!this.classList.contains('is-visible')) return;
      const height = this.offsetHeight;
      if (height === this.pageOffsetHeight) return;
      this.pageOffsetHeight = height;
      document.body.style.setProperty('--mobile-sticky-cart-height', `${height}px`);
    }

    clearPageOffset() {
      if (document.body.dataset.mobileStickyCartOwner !== this.sectionId) return;
      document.body.classList.remove('mobile-sticky-cart-visible');
      document.body.style.removeProperty('--mobile-sticky-cart-height');
      delete document.body.dataset.mobileStickyCartOwner;
      this.pageOffsetHeight = null;
    }

    moneyToCents(value) {
      if (value === null || value === undefined || value === '') return 0;
      const number = Number(String(value).replace(/,/g, ''));
      return Number.isFinite(number) ? Math.round(number * 100) : 0;
    }

    moneyTextToCents(value) {
      const normalized = String(value || '').replace(/[^\d.-]/g, '');
      if (!normalized) return 0;
      const number = Number(normalized);
      return Number.isFinite(number) ? Math.round(number * 100) : 0;
    }

    formatMoney(cents) {
      return `${Math.round((Number(cents) || 0) / 100).toLocaleString('ja-JP')}円`;
    }

    numberFromInput(value, fallback) {
      if (value === '' || value === null || value === undefined) return fallback;
      const number = Number(value);
      return Number.isFinite(number) ? number : fallback;
    }
  }

  customElements.define('mobile-sticky-cart', MobileStickyCart);
}
